/**
 * IssueDetailPage — "case file" layout.
 *
 * Structure (top → bottom):
 *   Page header:   ▸ CASE <8-char-id>  |  status pill · project chip · level chip
 *   Case meta:     level · count events · users · first_seen · last_seen · permalink
 *   Action bar:    [ ▸ analyse ]  [ open in sentry ↗ ]
 *   Accordions:    ANALYSIS (open when result exists) · EVENTS (collapsed) · STACK TRACE (collapsed)
 *   DetectiveFooter: visible while queued | running
 *
 * Issue data: passed via React Router location.state (set when navigating from
 * IssueListPage), falling back to a fresh list call filtered by id. This avoids
 * needing GET /api/issues/{id}/ which doesn't exist yet (Sprint 3 TODO).
 *
 * Analysis data: useAnalysis hook — polls while queued/running, caches result.
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import IosShareIcon from '@mui/icons-material/IosShare';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { issues as issuesApi } from '@/helpers/ApiHelper';
import { useAnalysis } from '@/hooks/useAnalysis';
import { AnalysisLoadingSkeleton } from '@/components/AnalysisLoadingSkeleton';
import { AnalysisPanel } from '@/components/AnalysisPanel';
import { InvestigationFeedback } from '@/components/InvestigationFeedback';
import { RetroAccordion } from '@/components/RetroAccordion';
import { ShareToClaudeDialog } from '@/components/ShareToClaudeDialog';
import type { Issue } from '@/types/issue';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type ChipColor = 'error' | 'warning' | 'info' | 'default';

function levelChipColor(level: string): ChipColor {
  switch (level) {
    case 'fatal':
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'default';
  }
}

function statusChipColor(status: string): ChipColor {
  switch (status) {
    case 'unresolved':
      return 'error';
    case 'resolved':
      return 'info';
    case 'ignored':
      return 'default';
    default:
      return 'default';
  }
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Issue loader — tries location.state first, then list API
// ---------------------------------------------------------------------------

interface IssueLoaderResult {
  issue: Issue | null;
  issueError: string | null;
}

function useIssueById(id: string): IssueLoaderResult {
  const location = useLocation();
  const passedIssue = (location.state as { issue?: Issue } | null)?.issue ?? null;

  const [issue, setIssue] = useState<Issue | null>(passedIssue);
  const [issueError, setIssueError] = useState<string | null>(null);

  const fetched = useRef(false);

  useEffect(() => {
    if (passedIssue || fetched.current) return;
    fetched.current = true;
    issuesApi
      .list({ page: 1 })
      .then((data) => {
        const found = data.results.find((r) => r.id === id) ?? null;
        if (found) {
          setIssue(found);
        } else {
          setIssueError('Issue not found.');
        }
      })
      .catch((err: unknown) => {
        setIssueError(err instanceof Error ? err.message : String(err));
      });
  }, [id, passedIssue]);

  return { issue, issueError };
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function IssueDetailPage() {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return <Navigate to="/issues" replace />;
  }

  return <IssueDetailPageInner issueId={id} />;
}

function IssueDetailPageInner({ issueId }: { issueId: string }) {
  const theme = useTheme();
  const { issue, issueError } = useIssueById(issueId);
  const { state, request } = useAnalysis(issueId);

  const isInFlight = state.status === 'queued' || state.status === 'running';
  const hasResult = state.status === 'completed';

  // ShareToClaudeDialog open state. Disabled until the analysis completes
  // so the trigger only mints against a result the engineer would actually
  // want to hand off.
  const [shareOpen, setShareOpen] = useState(false);
  const analysisId = state.status === 'completed' ? state.result.id : null;

  // Short ID for the case header: first 8 chars of issueId
  const shortId = issueId.slice(0, 8);

  // Current round + tool for the loading skeleton (agentic in-flight state)
  const currentRound =
    state.status === 'queued' || state.status === 'running'
      ? state.current_round
      : null;
  const currentTool =
    state.status === 'queued' || state.status === 'running'
      ? state.current_tool
      : null;

  // Dispatch buddy-context events when analysis state transitions.
  const prevAnalysisStatusRef = useRef<typeof state.status | null>(null);
  useEffect(() => {
    const prev = prevAnalysisStatusRef.current;
    const cur = state.status;
    prevAnalysisStatusRef.current = cur;
    if (prev === cur) return;
    let buddyStatus: 'running' | 'done' | 'failed' | null = null;
    if (cur === 'queued' || cur === 'running') {
      buddyStatus = 'running';
    } else if (cur === 'completed') {
      buddyStatus = 'done';
    } else if (cur === 'failed') {
      buddyStatus = 'failed';
    }
    if (buddyStatus) {
      // `job` carries the full issue UUID + type so DetectiveFooter's
      // polling reconciler can recover the terminal state if the user
      // navigates away before the analysis finishes. (analyses.get()
      // is keyed on issueId, hence type 'analysis' / id=issueId.)
      const job = { type: 'analysis', id: issueId } as const;
      window.dispatchEvent(
        new CustomEvent('sherlock:buddy-context', {
          detail: { kind: 'job', status: buddyStatus, identifier: shortId, job },
        }),
      );
    }
  }, [state.status, shortId, issueId]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleRunSonnet = () => {
    void request('claude-sonnet-4-6');
  };

  // ---------------------------------------------------------------------------
  // Analysis accordion content
  // ---------------------------------------------------------------------------

  function renderAnalysisContent() {
    switch (state.status) {
      case 'idle':
      case 'not_started':
        return (
          <Box
            sx={{
              py: 4,
              px: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No analysis yet. Click{' '}
              <Box
                component="span"
                sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'primary.main' }}
              >
                ▸ analyse
              </Box>{' '}
              to start.
            </Typography>
          </Box>
        );

      case 'queued':
      case 'running':
        return (
          <Box sx={{ px: 2, py: 2 }}>
            <AnalysisLoadingSkeleton
              currentRound={currentRound}
              currentTool={currentTool}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: 'block' }}
            >
              {state.status === 'queued' ? 'Queued' : 'Running'} — usually ~15s
            </Typography>
          </Box>
        );

      case 'completed':
        return (
          <Box sx={{ px: 2, py: 2 }}>
            <AnalysisPanel result={state.result} />
            {/* Feedback widget — sits below the analysis summary, above the
                REPL/tool-calls log inside AnalysisPanel (Sprint 12 Phase A).
                Bound to the analysis row, not the issue, so re-analyses
                start with a fresh feedback row. */}
            <InvestigationFeedback
              sourceType="analysis"
              sourceId={state.result.id}
            />
          </Box>
        );

      case 'failed':
        return (
          <Box sx={{ px: 2, py: 2 }}>
            <Alert severity="error" role="alert">
              {state.message}
            </Alert>
          </Box>
        );
    }
  }

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <>
      <Box sx={{ p: 3, pb: isInFlight ? 12 : 3 }}>
        {/* ---- Error loading issue ---- */}
        {issueError && (
          <Alert severity="error" role="alert" sx={{ mb: 2 }}>
            {issueError}
          </Alert>
        )}

        {/* ---- Page header ---- */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          {/* Left: case identifier */}
          <Typography
            component="h1"
            sx={{
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'primary.main',
              lineHeight: 1.2,
            }}
          >
            ▸ CASE {shortId}
          </Typography>

          {/* Right: status + project + level chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {issue?.status && (
              <Chip
                size="small"
                label={issue.status}
                color={statusChipColor(issue.status)}
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              />
            )}
            {issue?.project && (
              <Chip
                size="small"
                label={issue.project}
                variant="outlined"
                sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11 }}
              />
            )}
            {issue?.level && (
              <Chip
                size="small"
                label={issue.level}
                color={levelChipColor(issue.level)}
              />
            )}
          </Stack>
        </Stack>

        {/* ---- Issue title (full, below header) ---- */}
        {issue?.title && (
          <Typography
            variant="h5"
            sx={{ mb: 2, fontWeight: 600, wordBreak: 'break-word' }}
          >
            {issue.title}
          </Typography>
        )}

        {/* ---- Case meta strip ---- */}
        {issue && (
          <Box
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 12,
              color: 'text.secondary',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              mb: 3,
              lineHeight: 1.8,
            }}
          >
            <Box component="span">{issue.level}</Box>
            <Box component="span" sx={{ color: 'divider' }}> · </Box>
            <Box component="span">{issue.count.toLocaleString()} events</Box>
            {issue.user_count !== undefined && (
              <>
                <Box component="span" sx={{ color: 'divider' }}> · </Box>
                <Box component="span">{issue.user_count.toLocaleString()} users</Box>
              </>
            )}
            <Box component="span" sx={{ color: 'divider' }}> · </Box>
            <Box component="span">first {fmtDate(issue.first_seen)}</Box>
            <Box component="span" sx={{ color: 'divider' }}> · </Box>
            <Box component="span">last {fmtDate(issue.last_seen)}</Box>
            {issue.permalink && (
              <>
                <Box component="span" sx={{ color: 'divider' }}> · </Box>
                <Box
                  component="a"
                  href={issue.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                  }}
                >
                  sentry ↗
                </Box>
              </>
            )}
          </Box>
        )}

        {/* ---- Action bar ---- */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
          <Button
            variant="contained"
            disabled={isInFlight}
            onClick={handleRunSonnet}
            startIcon={
              isInFlight ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{
              minHeight: 44,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 13,
              textTransform: 'none',
              letterSpacing: '0.02em',
            }}
          >
            {isInFlight ? 'analysing…' : hasResult ? '▸ re-analyse' : '▸ analyse'}
          </Button>

          {issue?.permalink && (
            <Button
              variant="text"
              component="a"
              href={issue.permalink}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon fontSize="small" />}
              sx={{
                minHeight: 44,
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 12,
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              open in sentry
            </Button>
          )}

          <Button
            variant="text"
            disabled={!analysisId}
            onClick={() => setShareOpen(true)}
            endIcon={<IosShareIcon fontSize="small" />}
            sx={{
              minHeight: 44,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 12,
              textTransform: 'none',
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
          >
            share to claude ↗
          </Button>
        </Stack>

        {analysisId && (
          <ShareToClaudeDialog
            open={shareOpen}
            onClose={() => setShareOpen(false)}
            target={{ type: 'analysis', analysisId }}
          />
        )}

        {/* ---- Three RetroAccordion sections ---- */}
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {/* ANALYSIS — open when there is something to show (queued/running/completed/failed).
              The key prop forces a re-mount with the correct defaultOpen when status leaves
              idle/not_started so the accordion opens automatically for in-flight and completed
              analyses. */}
          <RetroAccordion
            key={
              state.status === 'idle' || state.status === 'not_started'
                ? 'analysis-closed'
                : 'analysis-open'
            }
            title="ANALYSIS"
            defaultOpen={
              state.status !== 'idle' && state.status !== 'not_started'
            }
            id="case-analysis"
          >
            {renderAnalysisContent()}
          </RetroAccordion>

          {/* EVENTS — collapsed by default */}
          <RetroAccordion
            title="EVENTS"
            subtitle="recent samples"
            defaultOpen={false}
            id="case-events"
          >
            <Box sx={{ px: 2, py: 3, color: 'text.secondary' }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 12,
                  color: 'text.disabled',
                }}
              >
                No recent events available.
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                Event-level sampling is a Sprint 3+ feature. Individual events will appear here once the endpoint is available.
              </Typography>
            </Box>
          </RetroAccordion>

          {/* STACK TRACE — collapsed by default */}
          <RetroAccordion
            title="STACK TRACE"
            defaultOpen={false}
            id="case-stack-trace"
          >
            <Box sx={{ px: 2, py: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 12,
                  color: 'text.disabled',
                }}
              >
                No stack trace available for this issue.
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                Stack trace data is not surfaced on Sentry group-level issues; it is available on individual events.
              </Typography>
            </Box>
          </RetroAccordion>
        </Box>
      </Box>

    </>
  );
}
